import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { s3GetUrl } from '../../service/s3Service';

// Condition type mappings (same as in create dialog)
const conditionTypeOptions = [
  { label: 'Number of Employees', value: 'employee_count' },
  { label: 'Vehicle Types', value: 'vehicle_type' },
  { label: 'Chemical Types', value: 'chemical_type' },
  { label: 'Equipment Types', value: 'equipment_type' },
  { label: 'Job Tasks', value: 'job_task' }
];

interface ChecklistTemplateViewDialogProps {
  visible: boolean;
  onHide: () => void;
  template: any;
}

export default function ChecklistTemplateViewDialog({ 
  visible, 
  onHide, 
  template 
}: ChecklistTemplateViewDialogProps) {
  const [iconUrl, setIconUrl] = useState<string | null>(null);

  // Load icon URL when template changes
  useEffect(() => {
    console.log('ChecklistTemplateViewDialog - useEffect triggered:', { 
      visible, 
      template, 
      hasTemplate: !!template,
      templateName: template?.name 
    });
    
    const loadIconUrl = async () => {
      if (template?.icon_s3_key) {
        try {
          const url = await s3GetUrl(template.icon_s3_key);
          setIconUrl(url);
        } catch (error) {
          console.error('Error loading icon URL:', error);
          setIconUrl(null);
        }
      } else {
        setIconUrl(null);
      }
    };

    if (visible && template) {
      loadIconUrl();
    }
  }, [template, visible]);

  console.log('ChecklistTemplateViewDialog render:', { 
    visible, 
    template, 
    hasTemplate: !!template,
    willRender: !!template 
  });

  if (!template) {
    console.log('ChecklistTemplateViewDialog: Not rendering - no template provided');
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFrequencyLabel = (type: string, intervalDays?: number) => {
    const frequencyMap: { [key: string]: string } = {
      hourly: 'Hourly',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      annually: 'Annually',
      custom: 'Custom'
    };
    
    const label = frequencyMap[type] || type;
    return type === 'custom' && intervalDays ? `${label} (${intervalDays} days)` : label;
  };

  // Format condition type to user-friendly label
  const getConditionTypeLabel = (conditionType: string) => {
    const option = conditionTypeOptions.find(opt => opt.value === conditionType);
    return option ? option.label : conditionType;
  };

  // Format operator to user-friendly text
  const getOperatorLabel = (operator: string) => {
    switch (operator) {
      case 'gt': return 'greater than';
      case 'gte': return 'greater than or equal to';
      case 'lt': return 'less than';
      case 'lte': return 'less than or equal to';
      case 'eq': return 'equal to';
      case 'ne': return 'not equal to';
      default: return operator;
    }
  };

  // Format condition value to user-friendly text
  const getConditionValueDisplay = (condition: any) => {
    if (!condition.condition_value) return 'Not specified';
    
    let values = condition.condition_value;
    if (typeof values === 'string') {
      try {
        values = JSON.parse(values);
      } catch {
        return values;
      }
    }
    
    if (!Array.isArray(values)) return String(values);
    if (values.length === 0) return 'None selected';
    if (values.includes('all') || values.includes('"all"')) return 'All options';
    
    return values.join(', ');
  };

  const renderHeader = () => (
    <div className="flex align-items-center gap-2">
      <i className="pi pi-eye text-primary" />
      <span className="font-bold text-xl">View Checklist Template</span>
    </div>
  );

  const renderBasicInfo = () => (
  <Card title="Basic Information" className="mb-4">
      <div className="grid">
        <div className="col-12 md:col-6">
          <div className="field">
            <label className="block text-900 font-medium mb-2">Template Name</label>
            <div className="text-900 font-semibold">{template.name}</div>
          </div>
        </div>
        
        <div className="col-12 md:col-6">
          <div className="field">
            <label className="block text-900 font-medium mb-2">Status</label>
            <Tag
              value={template.active ? 'Active' : 'Inactive'}
              severity={template.active ? 'success' : 'danger'}
            />
          </div>
        </div>

        {iconUrl && (
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block text-900 font-medium mb-2">Icon</label>
              <img 
                src={iconUrl} 
                alt="Template Icon" 
                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
                className="border-round"
              />
            </div>
          </div>
        )}

        <div className="col-12">
          <div className="field">
            <label className="block text-900 font-medium mb-2">Description</label>
            <div className="text-900" style={{ minHeight: '50px', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
              {template.description ? (
                <div dangerouslySetInnerHTML={{ __html: template.description }} />
              ) : (
                <span className="text-gray-500">No description provided</span>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <label className="block text-900 font-medium mb-2">Appointment</label>
            <div className="text-900">{template.appointment_name || 'N/A'}</div>
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <label className="block text-900 font-medium mb-2">Frequency</label>
            <div className="text-900">
              {getFrequencyLabel(template.frequency_type, template.frequency_interval_days)}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="field">
            <label className="block text-900 font-medium mb-2">Conditional Requirements</label>
            <div className="text-900">
              {template.conditional_requirement || 'No conditional requirements'}
            </div>
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <label className="block text-900 font-medium mb-2">Created</label>
            <div className="text-900">{formatDate(template.created_at)}</div>
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <label className="block text-900 font-medium mb-2">Last Updated</label>
            <div className="text-900">{formatDate(template.updated_at)}</div>
          </div>
        </div>
      </div>
    </Card>
  );

  const renderStructureInfo = () => (
    <Card title="Template Structure" className="mb-4">
      <div className="flex gap-4 mb-3">
        <div className="flex align-items-center gap-2">
          <Badge value={template.categories?.length || 0} severity="info" />
          <span>Categories</span>
        </div>
        <div className="flex align-items-center gap-2">
          <Badge 
            value={template.categories?.reduce((total: number, cat: any) => 
              total + (cat.questions?.filter((q: any) => q.question_text !== 'Photo Requirements').length || 0), 0) || 0} 
            severity="success" 
          />
          <span>Total Questions</span>
        </div>
      </div>

      {/* Photo Requirements Section */}
      {template.categories && template.categories.some((cat: any) => 
        cat.questions?.some((q: any) => q.question_text === 'Photo Requirements')
      ) && (
        <Card className="mb-3 border-1 border-primary-200">
          <div className="flex align-items-center gap-2 mb-3">
            <i className="pi pi-camera text-primary" />
            <span className="font-semibold text-lg text-primary">Photo Requirements</span>
          </div>
          {template.categories.map((category: any, catIndex: number) => {
            const photoReq = category.questions?.find((q: any) => q.question_text === 'Photo Requirements');
            if (!photoReq) return null;
            
            return (
              <div key={catIndex} className="mb-2">
                <div className="flex gap-2 align-items-center mb-2">
                  <span className="font-medium">{category.name}:</span>
                  {photoReq.required && (
                    <Tag value="Required" severity="warning" />
                  )}
                </div>
                {photoReq.question_instruction && (
                  <div 
                    className="text-600 text-sm ml-2"
                    dangerouslySetInnerHTML={{ __html: photoReq.question_instruction }}
                  />
                )}
              </div>
            );
          })}
        </Card>
      )}

      {template.categories && template.categories.length > 0 ? (
        <div className="mt-3">
          {template.categories.map((category: any, catIndex: number) => (
            <Card key={catIndex} className="mb-3 border-1 surface-border">
              <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-folder text-primary" />
                <span className="font-semibold text-lg">{category.name}</span>
                <Badge value={category.questions?.length || 0} severity="info" />
              </div>
              
              {category.description && (
                <div 
                  className="text-600 mb-3"
                  dangerouslySetInnerHTML={{ __html: category.description }}
                />
              )}

              {category.questions && category.questions.length > 0 && (
                <div className="pl-3">
                  <h6 className="text-900 font-medium mb-2">Questions:</h6>
                  {category.questions
                    .filter((question: any) => question.question_text !== 'Photo Requirements') // Filter out photo requirements pseudo-question
                    .map((question: any, qIndex: number) => (
                    <div key={qIndex} className="mb-3 p-3 surface-50 border-round">
                      <div className="flex align-items-start gap-2">
                        <span className="font-medium text-primary">Q{qIndex + 1}:</span>
                        <div className="flex-1">
                          <div 
                            className="m-0 mb-2"
                            dangerouslySetInnerHTML={{ __html: question.question_text || question.question_instruction || '' }}
                          />
                          <div className="flex gap-2">
                            {question.required && (
                              <Tag value="Required" severity="warning" />
                            )}
                            {question.non_compliant_options && 
                             question.non_compliant_options.length > 0 && (
                              <Tag 
                                value={`${question.non_compliant_options.length} Non-Compliant Options`} 
                                severity="info" 
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center text-500 py-4">
          <i className="pi pi-info-circle text-3xl mb-2" />
          <p>No categories defined for this template</p>
        </div>
      )}
    </Card>
  );

  const renderConditions = () => {
    if (!template.conditions || template.conditions.length === 0) {
      return null;
    }

    return (
      <Card title="Conditional Logic" className="mb-4">
        <div className="grid">
          {template.conditions.map((condition: any, index: number) => (
            <div key={index} className="col-12 md:col-6">
              <div className="p-3 surface-50 border-round">
                <div className="flex align-items-center gap-2 mb-2">
                  <i className="pi pi-filter text-primary" />
                  <span className="font-medium">Condition {index + 1}</span>
                </div>
                <div className="text-sm">
                  <p><strong>Type:</strong> {getConditionTypeLabel(condition.condition_type)}</p>
                  {condition.condition_type === 'employee_count' && condition.threshold_value && (
                    <p><strong>Rule:</strong> {getOperatorLabel(condition.operator || 'gt')} {condition.threshold_value} employees</p>
                  )}
                  {condition.condition_type !== 'employee_count' && (
                    <p><strong>Selection:</strong> {getConditionValueDisplay(condition)}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <Dialog
      visible={visible}
      style={{ width: '90vw', maxWidth: '1200px' }}
      header={renderHeader()}
      modal
      onHide={onHide}
      draggable={false}
      resizable={false}
      maximizable
      appendTo={typeof document !== 'undefined' ? document.body : undefined}
      baseZIndex={1000}
    >
      <div className="p-0">
        {renderBasicInfo()}
        {renderConditions()}
        {renderStructureInfo()}
      </div>
    </Dialog>
  );
}
